import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useOfficialStore } from '$/store/useOfficialStore';
import { useMyIpList } from '$/hooks/myIp/useMyIpList';
import { useMyIpSession } from '@/hooks/myIp/useMyIpSession';
import type { V3CanvasProjectItem } from '$/domains/workFlowV3/type';
import { getTeamProjectList } from '$/domains/workFlowV3/teamCanvasService';
import { getCanvasProjectListV3 } from '$/domains/workFlowV3/workFlowV3Service';
import { HomeIpCreationProvider } from '@/pages/--roboneo/home/components/HomeIpCreationEntry';
import CreateContentBox from '../CreateContentBox';
import HomeFeaturedCases from '../HomeFeaturedCases';
import HomeFooter from '../HomeFooter';
import MyProject from '../MyProject';
import MyIps from '../MyIps';
import NotLoggedIn from '../NotLoginIpProject';
import style from './index.module.scss';

const SECTION_GAP = 40;
const CARD_GAP = 12;
const IP_CREATE_CARD_WIDTH = 250;
const IP_CARD_WIDTH = 140;
const PROJECT_CREATE_CARD_WIDTH = 253;
const PROJECT_CARD_WIDTH = 270;
const PROJECT_DISPLAY_COUNT = 5;
const PROJECT_FETCH_COUNT = 5;
const MY_IPS_LEADING_PADDING = 40;
const MY_PROJECT_TRAILING_PADDING = 40;

type ProjectListStatus = 'idle' | 'loading' | 'loaded' | 'error';

/** 计算带新建入口的一组卡片总宽度，extraCount 为真实数据卡数量。 */
function getCardGroupWidth(createWidth: number, cardWidth: number, extraCount: number, extraPadding = 0) {
  if (extraCount <= 0) return createWidth + extraPadding;
  return createWidth + extraCount * (cardWidth + CARD_GAP) + extraPadding;
}

/** 按容器宽度优先展示 IP，并只给项目侧分配完整项目卡宽度。 */
function getIpProjectLayout(containerWidth: number, ipSlotCount: number, projectCount: number) {
  const availableWidth = Math.max(containerWidth - SECTION_GAP, 0);
  const cappedProjectCount = Math.min(projectCount, PROJECT_DISPLAY_COUNT);
  const reservedProjectCount = cappedProjectCount > 0 ? 1 : 0;
  const reservedProjectWidth = getCardGroupWidth(PROJECT_CREATE_CARD_WIDTH, PROJECT_CARD_WIDTH, reservedProjectCount, MY_PROJECT_TRAILING_PADDING);
  const desiredIpWidth = getCardGroupWidth(IP_CREATE_CARD_WIDTH, IP_CARD_WIDTH, ipSlotCount, MY_IPS_LEADING_PADDING);
  const ipWidth = Math.max(IP_CREATE_CARD_WIDTH + MY_IPS_LEADING_PADDING, Math.min(desiredIpWidth, availableWidth - reservedProjectWidth));
  const projectAvailableWidth = Math.max(availableWidth - ipWidth, 0);
  const projectFitCount = Math.max(0, Math.floor((projectAvailableWidth - PROJECT_CREATE_CARD_WIDTH) / (PROJECT_CARD_WIDTH + CARD_GAP)));
  const visibleProjectCount = Math.min(cappedProjectCount, projectFitCount);

  return {
    ipWidth,
    projectWidth: getCardGroupWidth(PROJECT_CREATE_CARD_WIDTH, PROJECT_CARD_WIDTH, visibleProjectCount, MY_PROJECT_TRAILING_PADDING),
    visibleProjectCount,
  };
}

/** 组装 RoboNeo 首页的主内容区域。 */
export default function HomeMain() {
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const clientOId = useOfficialStore((state) => state.clientOId);
  const isTeamEdition = Boolean(clientOId);
  const session = useMyIpSession();
  const ipState = useMyIpList(session.scopeKey, session.enabled && isUserLoggedIn);
  const ipProjectRef = useRef<HTMLDivElement>(null);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [ipSlotCount, setIpSlotCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [projects, setProjects] = useState<V3CanvasProjectItem[]>([]);
  const [projectListStatus, setProjectListStatus] = useState<ProjectListStatus>('idle');

  /** 记录首页 IP/项目容器宽度，作为卡片宽度分配的唯一来源。 */
  useEffect(() => {
    const container = ipProjectRef.current;
    if (!container) return undefined;
    const updateWidth = () => {
      const computedStyle = window.getComputedStyle(container);
      const horizontalPadding = parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);
      setLayoutWidth(Math.max(container.clientWidth - horizontalPadding, 0));
    };
    updateWidth();
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateWidth);
    observer?.observe(container);
    window.addEventListener('resize', updateWidth);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [ipState.status, isUserLoggedIn, projectListStatus, projects.length]);

  /** 子组件只上报数据数量，父级统一根据固定卡片尺寸计算左右区域。 */
  const handleIpSlotCountChange = useCallback((count: number) => {
    setIpSlotCount((current) => (current === count ? current : count));
  }, []);
  const handleProjectCountChange = useCallback((count: number) => {
    setProjectCount((current) => (current === count ? current : count));
  }, []);

  /** 在首页统一请求最近项目，确保 HomeMain 能判断 IP 与项目的组合空态。 */
  useEffect(() => {
    let canceled = false;
    if (!isUserLoggedIn) {
      setProjects([]);
      setProjectListStatus('idle');
      return undefined;
    }

    setProjectListStatus('loading');
    // 团队项目与个人项目归属不同接口，避免团队首页混入个人空间数据。
    const projectListRequest = isTeamEdition
      ? getTeamProjectList({ count: PROJECT_FETCH_COUNT })
      : getCanvasProjectListV3({
        tab: 'agent-team',
        cursor: 0,
        count: PROJECT_FETCH_COUNT,
      });

    projectListRequest
      .then((response) => {
        if (canceled) return;
        setProjects(
          (response.list ?? [])
            .filter((project) => Number(project.kind ?? 1) !== 2)
            .slice(0, PROJECT_DISPLAY_COUNT),
        );
        setProjectListStatus('loaded');
      })
      .catch(() => {
        if (canceled) return;
        setProjects([]);
        setProjectListStatus('error');
      });

    return () => {
      canceled = true;
    };
  }, [clientOId, isTeamEdition, isUserLoggedIn, session.scopeKey]);

  const layout = useMemo(
    () => getIpProjectLayout(layoutWidth, ipSlotCount, projectCount),
    [ipSlotCount, layoutWidth, projectCount],
  );
  const isIpListSettled = ipState.status !== 'idle' && ipState.status !== 'loading' && ipState.status !== 'error';
  const isIpListPending = ipState.status === 'idle' || ipState.status === 'loading';
  const isProjectListPending = projectListStatus === 'idle' || projectListStatus === 'loading';
  const showIpProjectPlaceholder = isUserLoggedIn && (isIpListPending || isProjectListPending);
  const showEmptyIpProject = isUserLoggedIn
    && isIpListSettled
    && projectListStatus === 'loaded'
    && ipState.list.length === 0
    && projects.length === 0;
  let ipProjectContent: React.ReactNode;
  if (showIpProjectPlaceholder) {
    ipProjectContent = <div className={style.ip_project_placeholder} aria-hidden="true" />;
  } else if (!isUserLoggedIn || showEmptyIpProject) {
    ipProjectContent = <NotLoggedIn />;
  } else {
    ipProjectContent = (
      <div className={style.ip_project_box} ref={ipProjectRef}>
        <MyIps width={layout.ipWidth} ipState={ipState} onSlotCountChange={handleIpSlotCountChange} />
        <MyProject
          width={layout.projectWidth}
          projects={projects}
          visibleProjectCount={layout.visibleProjectCount}
          onProjectCountChange={handleProjectCountChange}
        />
      </div>
    );
  }

  return (
    <HomeIpCreationProvider>
      <div className={style.home_main_container} style={{ paddingLeft: 'var(--sidebar-width)' }}>

        {/* 我的ip和我的项目 */}
        {ipProjectContent}

        {/* 为我的角色IP创作内容 */}
        <CreateContentBox />

        {/* 精选内容 */}
        <div className={style.selected_content_box}>
          <HomeFeaturedCases />
        </div>
        <HomeFooter />
      </div>
    </HomeIpCreationProvider>
  );
}
